import { useState, useCallback } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from './useAuth';
import { parseDriveUrl } from '../utils/driveUrl';
import { fetchDriveMetadata } from '../services/driveApi';
import { updateCard } from '../services/cardMetadataService';
import type { CardMetadata } from '../types/card';
import type { PageType } from '../types/nav';

function driveMimeToPageType(mimeType: string): PageType | null {
  switch (mimeType) {
    case 'application/vnd.google-apps.document': return 'doc';
    case 'application/vnd.google-apps.spreadsheet': return 'sheet';
    case 'application/vnd.google-apps.presentation': return 'slides';
    default: return null;
  }
}

function isStale(card: CardMetadata): boolean {
  if (!card.lastRefreshed) return true;
  const refreshedAt = card.lastRefreshed.toDate().getTime();
  return Date.now() - refreshedAt > 24 * 60 * 60 * 1000;
}

export function useDriveMetadata() {
  const { getGoogleAccessToken } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchAndCacheMetadata = useCallback(
    async (url: string, cardId: string) => {
      const parsed = parseDriveUrl(url);
      if (!parsed) return;

      const token = await getGoogleAccessToken();
      if (!token) return;

      setIsRefreshing(true);
      try {
        const result = await fetchDriveMetadata(parsed.docId, token);
        if (!result.ok) return;

        const { data } = result;
        const pageType = driveMimeToPageType(data.mimeType);

        await updateCard(cardId, {
          title: data.name,
          driveDocId: data.id,
          driveMimeType: data.mimeType,
          lastModified: Timestamp.fromDate(new Date(data.modifiedTime)),
          lastRefreshed: Timestamp.now(),
          ...(data.description ? { summary: data.description } : {}),
          ...(pageType ? { type: pageType } : {}),
        });
      } finally {
        setIsRefreshing(false);
      }
    },
    [getGoogleAccessToken],
  );

  const refreshCardMetadata = useCallback(
    async (card: CardMetadata) => {
      if (!card.driveDocId) return;
      if (!isStale(card)) return;

      const token = await getGoogleAccessToken();
      if (!token) return;

      setIsRefreshing(true);
      try {
        const result = await fetchDriveMetadata(card.driveDocId, token);
        if (!result.ok) return;

        const { data } = result;
        const pageType = driveMimeToPageType(data.mimeType);

        await updateCard(card.id, {
          title: data.name,
          driveMimeType: data.mimeType,
          lastModified: Timestamp.fromDate(new Date(data.modifiedTime)),
          lastRefreshed: Timestamp.now(),
          ...(data.description ? { summary: data.description } : {}),
          ...(pageType ? { type: pageType } : {}),
        });
      } finally {
        setIsRefreshing(false);
      }
    },
    [getGoogleAccessToken],
  );

  return { fetchAndCacheMetadata, refreshCardMetadata, isRefreshing };
}
